/**
 * NexusModal
 *
 * Interactive relationship-advisor modal for the Nexus agent.
 *
 * Flow:
 *   1. User describes what they want to accomplish in plain language.
 *   2. Nexus calls the LLM and returns a structured relationship suggestion.
 *   3. User reviews the suggestion and either approves (creates the edge) or dismisses.
 *
 * Unlike the generic AgentModal, NexusModal drives a multi-phase conversation
 * rather than a single "Run" action. It lives next to the item the user is
 * viewing and can write an emergent edge directly when approved.
 */

import { Fragment, useState, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  X,
  Loader2,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  RotateCcw,
  Network,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { AgentGlyph } from './AgentGlyph';
import { runNexusAgent } from '../../lib/llmNexusService';
import type { NexusSuggestion } from '../../lib/llmNexusService';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import { useTemplateAgents } from '../../hooks/useTemplateAgents';
import type { RelationshipEdgeType, DependencySubtype, ElaborationSubtype } from '../../types/prioritization/itemRelationships';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

// ── Relationship display helpers ───────────────────────────────────────────────

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  dependency:   { label: 'Dependency',   color: 'text-[var(--text-sec)]',   bg: 'bg-[var(--slate-dim)] border-[var(--slate)]' },
  prerequisite: { label: 'Prerequisite', color: 'text-[var(--text-sec)]',    bg: 'bg-[var(--slate-dim)] border-[var(--slate)]' },
  aggregation:  { label: 'Aggregation',  color: 'text-[var(--green)] dark:text-[var(--text-ter)]',bg: 'bg-[var(--green-dim)] border-[var(--green)]' },
  breakdown:    { label: 'Breakdown',    color: 'text-[var(--mustard)]',  bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]' },
  elaboration:  { label: 'Elaboration',  color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] border-[var(--slate)]' },
  related:      { label: 'Related',      color: 'text-[var(--text-sec)]',   bg: 'bg-[var(--surface)] border-[var(--rule)]' },
  cluster:      { label: 'Cluster',      color: 'text-cyan-700',   bg: 'bg-cyan-50 border-cyan-200' },
  package:      { label: 'Package',      color: 'text-[var(--mustard)]', bg: 'bg-[var(--mustard-dim)] border-[var(--mustard)]' },
  collection:   { label: 'Collection',   color: 'text-[var(--text-sec)]', bg: 'bg-[var(--slate-dim)] border-[var(--slate)]' },
};

const CONFIDENCE_BADGE: Record<string, string> = {
  high:   'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  low:    'bg-[var(--coral-dim)] text-[var(--coral)]',
};

function RoleLabel({ role, itemTitle }: { role?: 'source' | 'target'; itemTitle: string }) {
  if (!role) return null;
  return (
    <span className="text-xs text-[var(--text-ter)]">
      <span className="font-medium text-[var(--text-sec)]">"{itemTitle}"</span>
      {' '}is the{' '}
      <span className="font-medium text-[var(--text-sec)]">{role === 'source' ? 'upstream / enabling' : 'downstream / dependent'}</span>
      {' '}item in this relationship.
    </span>
  );
}

// ── Suggestion card ───────────────────────────────────────────────────────────

function SuggestionCard({
  suggestion,
  itemTitle,
}: {
  suggestion: NexusSuggestion;
  itemTitle: string;
}) {
  const typeInfo = TYPE_LABELS[suggestion.suggestedType] ?? TYPE_LABELS.related;

  return (
    <div className={` border p-4 space-y-3 ${typeInfo.bg}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Network className={`h-4 w-4 flex-shrink-0 ${typeInfo.color}`} />
          <span className={`text-sm font-semibold ${typeInfo.color}`}>{typeInfo.label}</span>
          {suggestion.suggestedSubtype && (
            <span className="text-xs font-medium text-[var(--text-ter)] bg-[var(--bg-raised)]/70 border border-[var(--rule)] px-1.5 py-0.5 rounded-full">
              {suggestion.suggestedSubtype.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${CONFIDENCE_BADGE[suggestion.confidence]}`}
        >
          {suggestion.confidence} confidence
        </span>
      </div>

      {suggestion.directionality === 'directed' && suggestion.thisItemRole && (
        <RoleLabel role={suggestion.thisItemRole} itemTitle={itemTitle} />
      )}

      {suggestion.mentionedTargetTitle && (
        <div className="flex items-center gap-1.5 text-xs text-[var(--text-sec)]">
          <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          Mentioned item:{' '}
          <span className="font-medium text-[var(--text-pri)]">"{suggestion.mentionedTargetTitle}"</span>
        </div>
      )}

      <p className="text-xs text-[var(--text-sec)] leading-relaxed">
        <span className="font-medium text-[var(--text-pri)]">Why: </span>
        {suggestion.rationale}
      </p>

      {suggestion.guidance && (
        <div className="flex items-start gap-2 bg-[var(--bg-raised)]/60 px-3 py-2 border border-white">
          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
          <p className="text-xs text-[var(--text-sec)] leading-relaxed">{suggestion.guidance}</p>
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export interface NexusModalProps {
  itemId: string;
  itemTitle: string;
  itemDescription?: string;
  itemCategory?: string;
  workspaceId: string;
  userId?: string;
  onClose: () => void;
}

type Phase = 'idle' | 'thinking' | 'suggested' | 'applied' | 'error';

export function NexusModal({
  itemId,
  itemTitle,
  itemDescription,
  itemCategory,
  workspaceId: _workspaceId,
  userId,
  onClose,
}: NexusModalProps) {
  // The Nexus agent's settings (agent_settings, migration 941): its model, prompt overlay and model parameters.
  const config = useTemplateAgents('propose_relationship').find((a) => a.def.agentType === 'nexus')?.settings ?? null;
  const createEdge = useItemRelationshipsStore((s) => s.createEdge);
  const existingEdges = useItemRelationshipsStore((s) => s.getEdgesForItem(itemId));

  const [phase, setPhase] = useState<Phase>('idle');
  const [userIntent, setUserIntent] = useState('');
  const [suggestion, setSuggestion] = useState<NexusSuggestion | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const edgeContext = existingEdges.slice(0, 6).map((e) => ({
    type: e.type,
    direction: e.directionality,
    otherItemTitle: undefined,
  }));

  const handleAsk = useCallback(async () => {
    if (!userIntent.trim()) return;
    setPhase('thinking');
    setErrorMsg(null);
    setSuggestion(null);

    try {
      const result = await runNexusAgent({
        itemTitle,
        itemDescription,
        itemCategory,
        userIntent,
        existingEdges: edgeContext,
        model: config?.model ?? DEFAULT_MODEL,
        systemPrompt: config?.systemPrompt ?? null,
      });

      setSuggestion(result);
      setPhase('suggested');
    } catch (err) {
      setPhase('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong');
    }
  }, [userIntent, itemTitle, itemDescription, itemCategory, edgeContext, config]);

  const handleApply = useCallback(async () => {
    if (!suggestion) return;

    // Only edge types map to item_relationship_edges; groups go to a different path.
    // For now, only apply if the type is a valid RelationshipEdgeType.
    const edgeTypes: RelationshipEdgeType[] = [
      'related', 'dependency', 'prerequisite', 'aggregation', 'elaboration', 'breakdown',
    ];
    if (!edgeTypes.includes(suggestion.suggestedType as RelationshipEdgeType)) {
      setPhase('applied');
      return;
    }

    setApplying(true);
    try {
      await createEdge({
        type: suggestion.suggestedType as RelationshipEdgeType,
        subtype: suggestion.suggestedSubtype as DependencySubtype | ElaborationSubtype | undefined,
        directionality: suggestion.directionality,
        status: 'candidate',
        declaredMode: 'emergent',
        sourceItemId: suggestion.thisItemRole === 'target' ? '' : itemId,
        targetItemId: suggestion.thisItemRole === 'target' ? itemId : '',
        rationale: suggestion.rationale,
        metadata: {
          confidence: suggestion.confidence === 'high' ? 0.9 : suggestion.confidence === 'medium' ? 0.6 : 0.3,
          nexus_intent: userIntent,
          mentioned_target_title: suggestion.mentionedTargetTitle ?? null,
          agent: 'nexus',
        },
        createdBy: userId,
      });
      setPhase('applied');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to create relationship');
    } finally {
      setApplying(false);
    }
  }, [suggestion, createEdge, itemId, userIntent, userId]);

  const handleRetry = useCallback(() => {
    setPhase('idle');
    setSuggestion(null);
    setErrorMsg(null);
    setTimeout(() => textareaRef.current?.focus(), 50);
  }, []);

  const isEnabled = config?.enabled === true;

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"   enterFrom="opacity-0"   enterTo="opacity-100"
          leave="ease-in duration-150"    leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="relative w-full max-w-md bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 overflow-hidden">

              {/* Header */}
              <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-emerald-500/10 to-teal-500/5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AgentGlyph
                      type="nexus"
                      colorScheme="emerald"
                      size={44}
                      pulse={phase === 'idle' || phase === 'suggested'}
                    />
                    <div>
                      <Dialog.Title className="text-base font-semibold text-[var(--text-pri)] leading-tight">
                        Nexus
                      </Dialog.Title>
                      <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">
                        Relationship advisor · describe your intent
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--bg-raised)]/60 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-5 py-4 space-y-4">

                {/* Disabled notice */}
                {!isEnabled && (
                  <div className="flex items-start gap-2 text-xs text-[var(--mustard)] bg-[var(--mustard-dim)] border border-[var(--mustard)] px-3 py-2.5">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      Nexus is disabled in workspace settings. Enable it under{' '}
                      <a href="/prioritization/settings/nexus-agent" className="underline font-medium">
                        Settings → Agents → Nexus
                      </a>
                      .
                    </span>
                  </div>
                )}

                {/* Item context pill */}
                <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] bg-[var(--surface)] border border-[var(--rule)] px-3 py-1.5">
                  <Network className="h-3 w-3 text-[var(--text-ter)] flex-shrink-0" />
                  <span className="text-[var(--text-ter)]">Advising on:</span>
                  <span className="font-medium text-[var(--text-sec)] truncate">{itemTitle}</span>
                </div>

                {/* ── Phase: idle or re-ask ── */}
                {(phase === 'idle' || phase === 'error') && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-[var(--text-sec)] mb-1.5">
                        What are you trying to accomplish?
                      </label>
                      <textarea
                        ref={textareaRef}
                        value={userIntent}
                        onChange={(e) => setUserIntent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault();
                            void handleAsk();
                          }
                        }}
                        disabled={!isEnabled}
                        rows={4}
                        placeholder={
                          'Describe the relationship in plain language.\n\nExamples:\n· "This item depends on the auth service being done first"\n· "This is part of the larger payments overhaul"\n· "This is a duplicate of the dark mode ticket"'
                        }
                        className="w-full text-sm border border-[var(--rule)] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none placeholder:text-[var(--text-ter)] disabled:opacity-60 disabled:bg-[var(--surface)]"
                        autoFocus
                      />
                      <p className="text-[10px] text-[var(--text-ter)] mt-1">Press ⌘↵ to ask</p>
                    </div>

                    {phase === 'error' && errorMsg && (
                      <div className="flex items-start gap-2 text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        {errorMsg}
                      </div>
                    )}
                  </>
                )}

                {/* ── Phase: thinking ── */}
                {phase === 'thinking' && (
                  <div className="flex flex-col items-center justify-center gap-3 py-8">
                    <AgentGlyph type="nexus" colorScheme="emerald" size={48} pulse />
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-[var(--text-sec)]">Analyzing your intent…</p>
                      <p className="text-xs text-[var(--text-ter)]">Reading the taxonomy and your item context</p>
                    </div>
                    <Loader2 className="h-4 w-4 animate-spin text-[var(--green)]" />
                  </div>
                )}

                {/* ── Phase: suggested ── */}
                {phase === 'suggested' && suggestion && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-[var(--green)]" />
                      <span className="text-xs font-semibold text-[var(--text-sec)]">Nexus suggests</span>
                    </div>
                    <SuggestionCard suggestion={suggestion} itemTitle={itemTitle} />

                    {errorMsg && (
                      <div className="flex items-start gap-2 text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                        {errorMsg}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Phase: applied ── */}
                {phase === 'applied' && (
                  <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
                    <CheckCircle2 className="h-10 w-10 text-[var(--green)]" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-[var(--text-pri)]">Relationship recorded</p>
                      <p className="text-xs text-[var(--text-ter)] leading-relaxed max-w-[280px]">
                        An emergent{' '}
                        <span className="font-medium">
                          {TYPE_LABELS[suggestion?.suggestedType ?? '']?.label ?? suggestion?.suggestedType}
                        </span>{' '}
                        edge has been created as a candidate.
                        Review it in the Relationships tab to confirm or adjust.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 pb-5 flex items-center justify-between gap-3">
                <div>
                  {phase === 'suggested' && (
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
                    >
                      <RotateCcw className="h-3 w-3" />
                      Try again
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                  >
                    {phase === 'applied' ? 'Close' : 'Cancel'}
                  </button>

                  {(phase === 'idle' || phase === 'error') && (
                    <button
                      type="button"
                      onClick={() => void handleAsk()}
                      disabled={!userIntent.trim() || !isEnabled}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Sparkles className="h-3.5 w-3.5" />
                      Ask Nexus
                    </button>
                  )}

                  {phase === 'suggested' && suggestion && (
                    <button
                      type="button"
                      onClick={() => void handleApply()}
                      disabled={applying}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-[var(--green)] hover:bg-[var(--green)] disabled:opacity-50 transition-colors"
                    >
                      {applying ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      )}
                      {applying ? 'Creating…' : 'Create this relationship'}
                    </button>
                  )}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
